import { memo, useCallback, useRef } from 'react';

import { useTrackAgentPillClicked } from '@air/analytics';
import { FileIcon } from '@air/design-icons';

import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentPillLeadingVisual } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPillLeadingVisual';
import { WorkspaceAgentPreviewCardCloseButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPreviewCardCloseButton';
import { WorkspaceAgentReferencePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentReferencePill';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { workspaceAgentPanelPill } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelPillChrome';
import { WorkspaceAgentAssetPreviewCard } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetPreviewCard';
import { useOpenWorkspaceAgentAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentAssetPill';
import { useWorkspaceAgentPreviewCardDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPreviewCardDisclosure';
import { usePrivateWorkspaceAgentPanelPillClips } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPillClipsProvider';
import { usePrivateWorkspaceAgentPillClipCardDetails } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentPillClipCardDetails';
import { type WorkspaceAgentAssetPill as WorkspaceAgentAssetPillMetadata } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { toWorkspaceAgentAssetPreviewCardContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentAssetPreviewCardContent';
import { isWorkspaceAgentDirectAssetMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentDirectAssetMoniker';

export type PrivateWorkspaceAgentAssetPillChipProps = {
  /** Mid-stream, a pill whose clip has not landed yet is not a broken one. */
  isStreaming: boolean;
  pill: WorkspaceAgentAssetPillMetadata;
};

const clipIdForAssetPill = ({
  getDirectAssetClipId,
  pill,
}: {
  getDirectAssetClipId: (pill: WorkspaceAgentAssetPillMetadata) => string | undefined;
  pill: WorkspaceAgentAssetPillMetadata;
}): string | undefined => pill.versionId ?? getDirectAssetClipId(pill);

/** A metadata pill that omitted its version stays a static chip. `@[asset:<assetId>]` loads a cover instead. */
const isStaticVersionlessAssetChip = ({
  directAsset,
  pill,
}: {
  directAsset: boolean;
  pill: WorkspaceAgentAssetPillMetadata;
}): boolean => pill.versionId === undefined && !directAsset;

const assetPillCardDetailsEnabled = ({
  clipId,
  isCardOpen,
}: {
  clipId: string | undefined;
  isCardOpen: boolean;
}): boolean => isCardOpen && Boolean(clipId);

const assetPillReferenceId = ({ assetId, clipId }: { assetId: string; clipId: string | undefined }): string =>
  clipId ?? assetId;

export const PrivateWorkspaceAgentAssetPillChip = memo(
  ({ isStreaming, pill }: PrivateWorkspaceAgentAssetPillChipProps) => {
    const {
      getDirectAssetClipId,
      getPillClip,
      isPending: isResolvingClips,
      isRefreshing: isRefreshingClips,
    } = usePrivateWorkspaceAgentPanelPillClips();
    const { openAssetClip, openAssetPill } = useOpenWorkspaceAgentAssetPill();
    const directAsset = isWorkspaceAgentDirectAssetMonikerPill(pill);
    const resolvedClipId = clipIdForAssetPill({ getDirectAssetClipId, pill });
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    /** The history surface has no editor to return to, so the chip itself is where the card came from. */
    const focusTrigger = useCallback(() => {
      triggerRef.current?.focus();
    }, []);

    // Read before the disclosure: a chip whose clip drops out of the batch has no card to keep open.
    const clip = getPillClip(pill);
    const { trackAgentPillClicked } = useTrackAgentPillClicked();
    const handleCardOpen = useCallback(() => {
      if (!clip) {
        return;
      }

      trackAgentPillClicked({ object: 'asset', object_count: 1, object_name: clip.name });
    }, [clip, trackAgentPillClicked]);

    const { closeCard, handleOpenChange, isCardOpen, isNavigatingAway, navigateFromCard } =
      useWorkspaceAgentPreviewCardDisclosure({
        focusNavigationOrigin: focusTrigger,
        hasSubject: !!clip,
        onCardOpen: handleCardOpen,
      });

    /** Only this path suppresses the close autofocus: Radix would pull focus off the asset modal. */
    const handleOpenAsset = useCallback(() => {
      navigateFromCard(() => {
        if (pill.versionId) {
          openAssetPill(pill);
          return;
        }

        if (resolvedClipId) {
          openAssetClip(resolvedClipId);
        }
      });
    }, [navigateFromCard, openAssetClip, openAssetPill, pill, resolvedClipId]);

    const { data: cardDetails } = usePrivateWorkspaceAgentPillClipCardDetails(
      { id: resolvedClipId },
      { enabled: assetPillCardDetailsEnabled({ clipId: resolvedClipId, isCardOpen }) },
    );

    if (isStaticVersionlessAssetChip({ directAsset, pill })) {
      return (
        <span
          className={workspaceAgentPanelPill()}
          // The raw id is for debugging and assertions only: it is never shown or announced.
          data-asset-id={pill.assetId}
          data-testid="WORKSPACE_AGENT_MESSAGE_VERSIONLESS_ASSET_PILL"
        >
          <FileIcon aria-hidden className="shrink-0 text-gray-10" size="xs" />
          <span className="truncate">Asset</span>
        </span>
      );
    }

    if (!clip) {
      // Placeholder data is the previous, narrower batch: an id it does not carry is still in flight.
      if (isStreaming || isResolvingClips || isRefreshingClips) {
        return <WorkspaceAgentPendingPill />;
      }

      return <WorkspaceAgentUnavailablePill kind="asset" referenceId={pill.assetId} />;
    }

    const content = toWorkspaceAgentAssetPreviewCardContent({
      boards: cardDetails?.boards ?? null,
      clip,
      commentCount: cardDetails?.openCommentCount ?? null,
    });

    return (
      <WorkspaceAgentReferencePill
        card={
          <WorkspaceAgentAssetPreviewCard
            closeButton={<WorkspaceAgentPreviewCardCloseButton onClose={closeCard} />}
            content={content}
            // The chip only draws a card once its clip has resolved, so the card never waits here.
            isLoading={false}
            onOpen={handleOpenAsset}
            referenceId={assetPillReferenceId({ assetId: pill.assetId, clipId: resolvedClipId })}
          />
        }
        isOpen={isCardOpen}
        leadingVisual={
          <WorkspaceAgentPillLeadingVisual
            assetType={content.type}
            ext={content.ext}
            pillThumbnailTestId="WORKSPACE_AGENT_MESSAGE_ASSET_PILL_THUMBNAIL"
            thumbnailUrl={content.thumbnailUrl}
          />
        }
        onOpenChange={handleOpenChange}
        shouldPreventCloseAutoFocus={isNavigatingAway}
        surface="history"
        testId="WORKSPACE_AGENT_MESSAGE_ASSET_PILL"
        title={content.title}
        triggerRef={triggerRef}
        // Not `…_ASSET_PILL_TRIGGER`: existing message-pill assertions name this id.
        triggerTestId="WORKSPACE_AGENT_MESSAGE_PILL"
      />
    );
  },
);

PrivateWorkspaceAgentAssetPillChip.displayName = 'PrivateWorkspaceAgentAssetPillChip';
